/* ----------------------------------------------------------------------------
 * Own-media (My uploads) picker model — the PURE, dependency-free logic behind
 * the content-studio "My uploads" tab. Extracted here so the whole flow is
 * unit-testable under `node --test --experimental-strip-types` (this repo's
 * DOM-free FE test convention). OwnMediaPicker.tsx is a thin view over these.
 *
 * ISOLATION invariant: this model NEVER constructs a workspace/domain selector.
 * Own-media is workspace-scoped by the server (the session key resolves the
 * workspace); the client sends no cross-workspace parameter and filterOwnUploads
 * only ever narrows a list the server already scoped — it can never widen it.
 *
 * HONESTY invariant: validation is a courtesy pre-check to avoid a wasted
 * upload; the BACKEND is authoritative and its rejection message is preferred
 * verbatim by describeUploadError. Progress is real byte progress where the
 * transport reports it, and honestly indeterminate (null) where it cannot.
 * -------------------------------------------------------------------------- */

import type { MediaKind, MediaSource, SavedAsset, StockItem } from '../../types/media.ts';

/* ---- accepted types + courtesy caps --------------------------------------- */
/** Image/logo MIME types the uploader offers. Backend re-validates first-hand. */
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
/** Video MIME types the uploader offers. */
export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'] as const;
/** Union used for the file input `accept` attribute + client pre-check. */
export const ACCEPTED_UPLOAD_TYPES: string[] = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];

/** Courtesy client caps ONLY — the backend enforces the real quota and returns
 *  an honest 413. Kept generous so we never reject something the backend allows. */
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024; // 15 MB (aligned to backend PHOTO_MAX_BYTES)
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024; // 100 MB (aligned to backend VIDEO_MAX_BYTES)

/** POST target. The api base already carries `/v1`, so this is `/v1/media/upload`. */
export const UPLOAD_PATH = '/media/upload';

export interface UploadValidation {
  ok: boolean;
  /** Honest human reason when ok=false; null when ok. */
  reason: string | null;
  /** Resolved kind when the type is recognised; null when unsupported. */
  kind: MediaKind | null;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0–100, or null when the transport cannot compute it (honestly indeterminate). */
  percent: number | null;
}

/** Map a MIME type to our media kind, or null when it is not an accepted type. */
export function kindForMime(mime: string): MediaKind | null {
  if ((ACCEPTED_IMAGE_TYPES as readonly string[]).includes(mime)) return 'photo';
  if ((ACCEPTED_VIDEO_TYPES as readonly string[]).includes(mime)) return 'video';
  return null;
}

/** Client-side pre-check (courtesy). Rejects an obviously-unsupported type or an
 *  over-cap file BEFORE a wasted upload; the backend remains authoritative. */
export function validateUploadFile(file: { name: string; type: string; size: number }): UploadValidation {
  const kind = kindForMime(file.type);
  if (kind === null) {
    return {
      ok: false,
      kind: null,
      reason: 'That file type isn’t supported. Use PNG, JPEG, WebP or GIF images, or MP4, WebM or MOV video.',
    };
  }
  if (file.size <= 0) {
    return { ok: false, kind, reason: 'That file looks empty. Choose a different file.' };
  }
  const cap = kind === 'video' ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
  if (file.size > cap) {
    return { ok: false, kind, reason: `That file is too large (max ${formatBytes(cap)}). Choose a smaller file.` };
  }
  return { ok: true, kind, reason: null };
}

/** Human-readable byte size. */
export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 ? Math.round(v) : Math.round(v * 10) / 10} ${units[i]}`;
}

/** Compute an honest progress percent, or null when the total is unknown. */
export function toUploadProgress(loaded: number, total: number, lengthComputable: boolean): UploadProgress {
  const percent =
    lengthComputable && total > 0 ? Math.max(0, Math.min(100, Math.round((loaded / total) * 100))) : null;
  return { loaded, total, percent };
}

/** Duck-typed error shape (matches ApiError without importing it, so this module
 *  stays runtime-dependency-free and node-testable). */
export interface UploadErrorLike {
  status?: number;
  code?: string;
  message?: string;
}

/** Turn an upload failure into an honest, user-facing message. The backend's own
 *  message is PREFERRED verbatim (it is the source of truth for validation);
 *  status/code only drive the fallback when the backend gave no message. */
export function describeUploadError(err: unknown): string {
  const e = (err ?? {}) as UploadErrorLike;
  const status = typeof e.status === 'number' ? e.status : undefined;
  const code = typeof e.code === 'string' ? e.code : undefined;
  const message = typeof e.message === 'string' ? e.message.trim() : '';

  // Network / transport failure — never a backend message.
  if (status === 0 || code === 'network_error') {
    return 'Upload didn’t complete. This can happen if the file is very large or the connection dropped — check the file and try again.';
  }
  // Prefer the backend's honest message when present (validation truth).
  if (message !== '' && message.toLowerCase() !== 'error') return message;

  switch (code) {
    case 'empty_upload':
      return 'No file was selected.';
    case 'unsupported_type':
      return 'That file type isn’t supported.';
    case 'too_large':
    case 'upload_too_large':
      return 'That file is too large. Choose a smaller file.';
    case 'storage_failed':
    case 'persist_failed':
      return 'We couldn’t store that file. Try again.';
    default:
      break;
  }
  switch (status) {
    case 404:
      return 'Uploading isn’t available yet — it hasn’t been enabled for this workspace.';
    case 413:
      return 'That file is too large. Choose a smaller file.';
    case 415:
      return 'That file type isn’t supported.';
    case 403:
      return 'Uploading isn’t available right now. Refresh the page and try again.';
    case 400:
    case 422:
      return 'That file couldn’t be uploaded. Check the file and try again.';
    default:
      return 'Upload failed. Please try again.';
  }
}

/* ---- own-only list narrowing (isolation) ---------------------------------- */
/** An asset is an own upload when the server marked it source/provider 'upload'.
 *  Server default (null) reads as 'stock', so this NEVER promotes a stock row. */
export function isOwnUpload(asset: Pick<SavedAsset, 'source' | 'provider'>): boolean {
  return asset.source === 'upload' || asset.provider === 'upload';
}

/** Narrow a server-scoped saved-asset list to only the workspace's OWN uploads.
 *  Pure narrowing — cannot widen or reach another workspace. */
export function filterOwnUploads(assets: SavedAsset[]): SavedAsset[] {
  return assets.filter(isOwnUpload);
}

/** Display label for an own asset: filename first, then attribution, then a
 *  stable synthetic (never a fabricated human name). */
export function assetDisplayName(asset: SavedAsset): string {
  if (asset.originalFilename && asset.originalFilename.trim() !== '') return asset.originalFilename;
  if (asset.attribution && asset.attribution.trim() !== '') return asset.attribution;
  return `${asset.kind === 'video' ? 'Video' : 'Image'}${asset.id != null ? ` #${asset.id}` : ''}`;
}

/* ---- placement via the EXISTING onSaved contract -------------------------- */
/** Build the POST contract descriptor (proves the picker targets the locked
 *  endpoint without a live call). */
export function buildUploadRequest(): { method: 'POST'; path: string } {
  return { method: 'POST', path: UPLOAD_PATH };
}

/** Adapt a saved own-media asset into the StockItem shape the studio's existing
 *  `onSaved(item, savedId)` handler already consumes — so the studio places /
 *  replaces own media through the SAME contract as stock, unchanged. A fresh
 *  local object URL (from the just-uploaded File) is preferred for preview; a
 *  server-resolved previewUrl is used for reused assets; empty when neither. */
export function synthesizeStockItemFromAsset(asset: SavedAsset, previewUrl: string | null): StockItem {
  const source: MediaSource = asset.source ?? 'upload';
  // A REAL attribution (e.g. a Pexels credit) wins; else the original
  // filename (honest for an own upload); else EMPTY — never the synthetic
  // 'Image #N' label, which must not be persisted as if it were a credit.
  const attribution =
    asset.attribution && asset.attribution.trim() !== ''
      ? asset.attribution
      : asset.originalFilename && asset.originalFilename.trim() !== ''
        ? asset.originalFilename
        : '';
  return {
    provider: asset.provider || 'upload',
    providerAssetId: asset.providerAssetId,
    kind: asset.kind,
    previewUrl: previewUrl ?? asset.previewUrl ?? '',
    previewVideoUrl: asset.kind === 'video' ? asset.previewUrl : null,
    renditions: [],
    photographer: source === 'upload' ? 'Your upload' : (asset.photographer ?? 'Unknown'),
    photographerUrl: asset.photographerUrl,
    sourceUrl: asset.sourceUrl,
    attribution,
    width: asset.width,
    height: asset.height,
    alt: null,
    durationSecs: null,
  };
}

export interface OwnSelectDeps {
  /** The picker's EXISTING placement contract — unchanged. */
  onSaved?: (item: StockItem, savedId: number | null) => void;
}

/** Place (or REPLACE — re-firing overwrites the studio slot, no page rebuild) an
 *  own asset through the existing onSaved contract. Returns true when a handler
 *  was invoked (false in a browse-only context with no handler). */
export function selectOwnAsset(asset: SavedAsset, previewUrl: string | null, deps: OwnSelectDeps): boolean {
  if (!deps.onSaved) return false;
  deps.onSaved(synthesizeStockItemFromAsset(asset, previewUrl), asset.id);
  return true;
}
